import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export const KIOSKSPLASHSCREENPAGE_LEGACY_FIELD_ORDER = [
  "link_url",
  { type: 'separator' },
  "background_image",
  "background_video",
  { type: 'messageUtil', options: {"type":"info","message":"kiosk.splashScreen.page.fields.videoFallbackInfo"} },
  { type: 'messageUtil', options: {"type":"warn","padding":true,"message":"kiosk.splashScreen.page.fields.videoFormatWarning"} },
  "show_footer",
  "label_call_to_action",
  "text_color",
  "background_color",
  "background_opacity"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskSplashScreenPageDescriptor: _generatedKioskSplashScreenPageDescriptor,
  kioskSplashScreenPageBlocDefinition,
  kioskSplashScreenPageDashboardLayout,
  kioskSplashScreenPageConfig,
  kioskSplashScreenPageResolveConfig,
  kioskSplashScreenPageProperties,
} = doodBlockConfig(
  {
    name: "Kiosk - Splash screen page",
    internal_name: "Kiosk - Splash screen page",
    url: "splash",
    defaultParent: [/^Main Layout$/i],
    componentType: 'KioskSplashScreenPageComponent',
    category: 'page',
    description: "Kiosk - Splash screen page.",
    keywords: [],
    isRootBlock: true,
    sharedFields: {}
  },

  () => ({
    link_url: propertyValue({
      type: 'link',
      value: '',
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.link-url.label",
        help: "pages.blocks.kiosk-splash-screen-page.link-url.help",
      }
    }),
    show_footer: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.show-footer.label",
        help: "pages.blocks.kiosk-splash-screen-page.show-footer.help",
      }
    }),
    label_call_to_action: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.label-call-to-action.label",
      }
    }),
    background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-100"},
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.background-color.label",
      }
    }),
    background_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.background-image.label",
        help: "pages.blocks.kiosk-splash-screen-page.background-image.help",
      }
    }),
    background_video: propertyValue({
      type: 'video_file',
      value: '',
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.background-video.label",
        help: "pages.blocks.kiosk-splash-screen-page.background-video.help",
      }
    }),
    text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-800"},
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.text-color.label",
      }
    }),
    background_opacity: propertyValue({
      type: 'select',
      value: 1,
      options: {
        label: "pages.blocks.kiosk-splash-screen-page.background-opacity.label",
        help: "pages.blocks.kiosk-splash-screen-page.background-opacity.help",
        "choices": [
          {
            "label": "0%",
            "value": 0
          },
          {
            "label": "10%",
            "value": 0.1
          },
          {
            "label": "20%",
            "value": 0.2
          },
          {
            "label": "30%",
            "value": 0.3
          },
          {
            "label": "40%",
            "value": 0.4
          },
          {
            "label": "50%",
            "value": 0.5
          },
          {
            "label": "60%",
            "value": 0.6
          },
          {
            "label": "70%",
            "value": 0.7
          },
          {
            "label": "80%",
            "value": 0.8
          },
          {
            "label": "90%",
            "value": 0.9
          },
          {
            "label": "100%",
            "value": 1
          }
        ]
      }
    })
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.kiosk-splash-screen-page.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.kiosk-splash-screen-page.fieldset.click-action.label",
            fields: ["link_url"],
          },
          {
            legend: "pages.blocks.kiosk-splash-screen-page.fieldset.background.label",
            fields: ["background_image", "background_video"],
          },
          {
            legend: "pages.blocks.kiosk-splash-screen-page.fieldset.footer.label",
            fields: [
              "show_footer",
              { prop: "label_call_to_action", visibleWhen: { show_footer: true as const } },
              { prop: "text_color", visibleWhen: { show_footer: true as const } },
              { prop: "background_color", visibleWhen: { show_footer: true as const } },
              { prop: "background_opacity", visibleWhen: { show_footer: true as const } },
            ],
          },
        ],
      },
    ],
  })
);

export function kioskSplashScreenPageDescriptor(): IBlockDescriptor;
export function kioskSplashScreenPageDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskSplashScreenPageDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskSplashScreenPageDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedKioskSplashScreenPageDescriptor, kioskSplashScreenPageProperties, fieldOrder);
}

export {
  kioskSplashScreenPageBlocDefinition,
  kioskSplashScreenPageDashboardLayout,
  kioskSplashScreenPageProperties,
  kioskSplashScreenPageConfig,
  kioskSplashScreenPageResolveConfig,
};

export type KioskSplashScreenPageConfig = typeof kioskSplashScreenPageConfig;
